import React, {
    createContext,
    useContext,
    useState,
    useMemo,
    useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { CONSENT_CONTRACT_PATHS } from '../../../constants/consent-contracts-paths';
import { JOB_MANAGER_PATHS } from '../../../constants/job-manager-paths';
import { WALLETS_AND_GROUPS_PATHS } from '../../../constants/wallets-and-groups-paths';
import { SMART_CONTRACT_PATHS } from 'constants/smart-contracts-paths';
import { DICTIONARY_PATHS } from 'constants/dictionary-paths';
import { AlertContextType, AlertProps } from './alert-context-helper';
import { CustomAlert } from './CustomAlert';
import { ErrorDetailsDialog } from './ErrorDetailsDialog';

const AlertContext = createContext<AlertContextType | undefined>(undefined);

const AlertProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [displayAlert, setDisplayAlert] = useState<boolean>(false);
    const [alertProps, setAlertProps] = useState<AlertProps | null>(null);
    const [errorDetails, setErrorDetails] = useState<string>('');
    const [displayErrorDetails, setDisplayErrorDetails] =
        useState<boolean>(false);
    const location = useLocation();

    const showAlert = (props: AlertProps): void => {
        setAlertProps(props);
        setDisplayAlert(true);
    };

    const hideAlert = (): void => {
        setAlertProps(null);
        setDisplayAlert(false);
    };

    const showErrorDetails = (): void => {
        setDisplayErrorDetails(true);
    };

    const onDialogCloseButtonClick = (): void => {
        setDisplayErrorDetails(false);
    };

    const value = useMemo(
        () => ({ alertProps, showAlert, hideAlert, showErrorDetails }),
        [alertProps]
    );

    useEffect(() => {
        if (alertProps !== null) {
            setErrorDetails(alertProps?.details ?? '');
        }
    }, [alertProps]);

    useEffect(() => {
        const smartContractPathPattern = new RegExp(
            `^${SMART_CONTRACT_PATHS.LANDING_PAGE}/[^/]+/[^/]+$`
        );
        if (
            location.pathname !== JOB_MANAGER_PATHS.LANDING_PAGE &&
            location.pathname !==
                WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH &&
            location.pathname !== CONSENT_CONTRACT_PATHS.LANDING_PAGE &&
            location.pathname !== SMART_CONTRACT_PATHS.LANDING_PAGE &&
            location.pathname !== DICTIONARY_PATHS.LANDING_PAGE &&
            !smartContractPathPattern.test(location.pathname)
        ) {
            hideAlert();
        }
    }, [location.pathname]);

    return (
        <AlertContext.Provider value={value}>
            {alertProps &&
                createPortal(
                    <>
                        {displayAlert && (
                            <CustomAlert
                                {...alertProps}
                                onShowDetailsClicked={showErrorDetails}
                            />
                        )}
                        {displayErrorDetails && errorDetails && (
                            <ErrorDetailsDialog
                                errorDetails={errorDetails}
                                handleOnClose={onDialogCloseButtonClick}
                            />
                        )}
                    </>,
                    document.getElementById('root')!
                )}
            {children}
        </AlertContext.Provider>
    );
};

const useAlert = (): AlertContextType => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

export { AlertProvider, useAlert };
